import { jsPDF } from "jspdf";

/**
 * Generate a Heritage Dahlias branded invoice / receipt PDF.
 *
 * Visual language matches the customer-facing HTML invoice at
 * /api/customer/orders/{id}/invoice/html.
 *  • Serif (Times) typography throughout
 *  • Heritage seal in the header
 *  • GST 15% line (NZ retail prices are GST-inclusive, back-calculated)
 *  • NZBN + bank details
 *  • Auto-switches to RECEIPT with a "PAID" stamp when `paid` is true
 *
 * Returns { filename, base64, total } so callers can persist the PDF.
 */

const BUSINESS = {
  name: "HERITAGE DAHLIAS NZ LIMITED",
  address: "78 Gorrie Road, Mangaroa Valley",
  postcode: "Upper Hutt, New Zealand",
  email: "hello@heritagedahlias.nz",
  website: "heritagedahlias.nz",
  nzbn: "9429053649812",
  bank: "38-9027-0833898-00",
};

const formatNZ = (n) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0,
  );

const formatDateNZ = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
};

// Heritage colours (RGB tuples for jsPDF)
const INK     = [62, 35, 70];   // #3E2346 aubergine
const BERRY   = [110, 30, 58];  // #6E1E3A
const SAGE    = [135, 150, 107];
const FOREST  = [47, 74, 42];
const PARCH   = [240, 227, 209];
const MUTED   = [120, 110, 100];

export function generateBoxInvoicePdf({
  invoiceNumber,
  issueDate,
  dueDate,
  customer = {},
  items = [],
  subtotal,             // optional, back-calculated from total if omitted
  creditApplied = 0,
  total = 0,
  notes = "",
  paid = false,
  download = true,      // when false, render silently (no forced download)
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  // Use the built-in serif throughout
  doc.setFont("times", "normal");
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;

  const docLabel = paid ? "RECEIPT" : "INVOICE";
  // GST is 15%, prices GST-inclusive: ex-GST = total × 20/23, GST = total × 3/23
  const gstAmount  = Math.round((Number(total) || 0) * 300 / 23) / 100;
  const subTotalEx = Math.round(((Number(total) || 0) - gstAmount) * 100) / 100;
  const itemsSubtotalIncl = Number.isFinite(subtotal) && subtotal > 0
    ? subtotal
    : items.reduce((s, it) => s + (Number(it.qty) || 1) * (Number(it.unit_price) || 0), 0);

  // ───── Header ─────
  doc.setTextColor(...INK);
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text(BUSINESS.name, M, 60);
  doc.setFont("times", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  doc.text(BUSINESS.address, M, 75);
  doc.text(BUSINESS.postcode, M, 88);
  doc.text(`NZBN ${BUSINESS.nzbn} · GST registered`, M, 101);
  doc.text(BUSINESS.email + "  ·  " + BUSINESS.website, M, 114);

  // RECEIPT / INVOICE big right
  doc.setTextColor(...INK);
  doc.setFont("times", "bold");
  doc.setFontSize(32);
  doc.text(docLabel, W - M, 70, { align: "right" });

  doc.setFontSize(10);
  doc.setFont("times", "normal");
  doc.setTextColor(...MUTED);
  if (invoiceNumber) doc.text(`No. ${invoiceNumber}`, W - M, 88, { align: "right" });
  if (issueDate)     doc.text(`Issued ${formatDateNZ(issueDate)}`, W - M, 102, { align: "right" });
  if (!paid && dueDate) doc.text(`Due ${formatDateNZ(dueDate)}`, W - M, 116, { align: "right" });

  // Paid / Awaiting pill
  const pillX = W - M;
  const pillY = paid ? 128 : 128;
  const pillW = 70, pillH = 18;
  if (paid) {
    doc.setFillColor(...FOREST);
    doc.setTextColor(251, 230, 216);
    doc.roundedRect(pillX - pillW, pillY - 14, pillW, pillH, 9, 9, "F");
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.text("PAID", pillX - pillW / 2, pillY - 2, { align: "center" });
  } else {
    doc.setFillColor(244, 208, 111);
    doc.setTextColor(...BERRY);
    doc.roundedRect(pillX - 130, pillY - 14, 130, pillH, 9, 9, "F");
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.text("AWAITING PAYMENT", pillX - 65, pillY - 2, { align: "center" });
  }

  // Divider
  doc.setDrawColor(...INK);
  doc.setLineWidth(1);
  doc.line(M, 146, W - M, 146);

  // ───── Bill To ─────
  doc.setFont("times", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BERRY);
  doc.text("BILLED TO", M, 176);
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  let y = 192;
  doc.text(customer.name || customer.email || "Heritage HQ Customer", M, y);
  y += 14;
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  if (customer.email) { doc.text(customer.email, M, y); y += 13; }
  if (customer.phone) { doc.text(customer.phone, M, y); y += 13; }
  if (customer.address) {
    const addrLines = doc.splitTextToSize(customer.address, (W / 2) - M);
    addrLines.forEach((line) => { doc.text(line, M, y); y += 13; });
  }
  if (customer.heritage_id) {
    doc.setFont("times", "bold");
    doc.setTextColor(...BERRY);
    doc.text(`Customer ID: ${customer.heritage_id}`, M, y);
    y += 13;
  }

  // ───── Items table ─────
  const tableTop = Math.max(y + 24, 290);
  doc.setFillColor(...INK);
  doc.rect(M, tableTop, W - M * 2, 26, "F");
  doc.setFont("times", "bold");
  doc.setFontSize(9);
  doc.setTextColor(251, 246, 233);
  const COL_QTY  = W - M - 220;
  const COL_UNIT = W - M - 110;
  const COL_AMT  = W - M - 12;
  doc.text("ITEM", M + 12, tableTop + 17);
  doc.text("QTY", COL_QTY, tableTop + 17, { align: "right" });
  doc.text("UNIT (INCL GST)", COL_UNIT, tableTop + 17, { align: "right" });
  doc.text("LINE", COL_AMT, tableTop + 17, { align: "right" });

  let rowY = tableTop + 26;
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  items.forEach((it, i) => {
    const desc = it.description || it.name || "";
    const descLines = doc.splitTextToSize(desc, (COL_QTY - M - 24));
    const rowHeight = Math.max(28, descLines.length * 14 + 12);
    if (i % 2 === 0) {
      doc.setFillColor(...PARCH);
      doc.rect(M, rowY, W - M * 2, rowHeight, "F");
    }
    doc.setTextColor(...INK);
    doc.text(descLines, M + 12, rowY + 17);
    const qty   = Number(it.qty) || 1;
    const unit  = Number(it.unit_price) || 0;
    const amount = qty * unit;
    doc.text(String(qty), COL_QTY, rowY + 17, { align: "right" });
    doc.text(formatNZ(unit), COL_UNIT, rowY + 17, { align: "right" });
    doc.text(formatNZ(amount), COL_AMT, rowY + 17, { align: "right" });
    rowY += rowHeight;
  });

  // ───── Totals (right column) ─────
  rowY += 14;
  const totalsX = W - M - 200;
  doc.setDrawColor(...SAGE);
  doc.setLineWidth(0.4);
  doc.line(totalsX, rowY, W - M, rowY);
  rowY += 16;
  doc.setFont("times", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...MUTED);
  doc.text("Subtotal (excl GST)", totalsX, rowY);
  doc.text(formatNZ(subTotalEx), W - M, rowY, { align: "right" });
  rowY += 15;
  doc.text("GST 15%", totalsX, rowY);
  doc.text(formatNZ(gstAmount), W - M, rowY, { align: "right" });
  if (Number(creditApplied) > 0) {
    rowY += 15;
    doc.setTextColor(...BERRY);
    doc.text("Heritage Credit", totalsX, rowY);
    doc.text(`-${formatNZ(creditApplied)}`, W - M, rowY, { align: "right" });
  }
  rowY += 20;
  doc.setDrawColor(...INK);
  doc.setLineWidth(1.5);
  doc.line(totalsX, rowY, W - M, rowY);
  rowY += 22;
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...INK);
  doc.text(paid ? "TOTAL PAID (NZD)" : "TOTAL DUE (NZD)", totalsX, rowY);
  doc.setTextColor(...BERRY);
  doc.setFontSize(17);
  doc.text(formatNZ(total), W - M, rowY, { align: "right" });

  // Diagonal PAID stamp (subtle)
  if (paid) {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.13 }));
    doc.setTextColor(...FOREST);
    doc.setFont("times", "bold");
    doc.setFontSize(120);
    const cx = W / 2;
    const cy = H / 2;
    // jsPDF doesn't have native rotated text, fake it with a quick matrix
    doc.text("PAID", cx, cy, { align: "center", angle: -18 });
    doc.restoreGraphicsState();
  }

  // ───── Notes & Payment ─────
  rowY += 36;
  if (notes && notes.trim()) {
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BERRY);
    doc.text("NOTES", M, rowY);
    rowY += 14;
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.splitTextToSize(notes, W - M * 2).forEach((line) => {
      doc.text(line, M, rowY);
      rowY += 13;
    });
    rowY += 8;
  }

  if (!paid) {
    // Bank-deposit instructions block
    doc.setFillColor(...PARCH);
    doc.roundedRect(M, rowY, W - M * 2, 86, 4, 4, "F");
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BERRY);
    doc.text("PAYMENT INSTRUCTIONS , BANK DEPOSIT", M + 12, rowY + 18);
    doc.setFont("times", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...INK);
    doc.text(`Account name: ${BUSINESS.name}`, M + 12, rowY + 36);
    doc.setFont("courier", "bold");
    doc.text(BUSINESS.bank, M + 12, rowY + 52);
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(`Reference: ${invoiceNumber || ""} · Particulars: ${customer.name || customer.email || ""}`, M + 12, rowY + 70);
    rowY += 96;
  }

  // ───── Footer ─────
  const footerY = H - 56;
  doc.setDrawColor(...SAGE);
  doc.setLineWidth(0.4);
  doc.line(M, footerY, W - M, footerY);
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text("Thank you for supporting a small family flower farm.", W / 2, footerY + 18, { align: "center" });
  doc.setFont("times", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...INK);
  doc.text(
    `${BUSINESS.name}  ·  NZBN ${BUSINESS.nzbn}  ·  ${BUSINESS.website}`,
    W / 2, footerY + 32, { align: "center" },
  );

  const safe = (customer.name || customer.email || "customer").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const filename = `heritage-${docLabel.toLowerCase()}-${invoiceNumber || "vip-box"}-${safe}.pdf`;
  if (download) doc.save(filename);
  const dataUri = doc.output("datauristring");
  const base64 = dataUri.split(",")[1] || "";
  return { filename, base64, total, gst: gstAmount, subtotal_ex: subTotalEx };
}
